import React, { useState } from "react";
import { ClipboardCheck, Package, Plus, Trash2, Camera, Loader2, CheckCircle2 } from "lucide-react";
import { maintenanceApi } from "@/services/api";
import { apiError } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PhotoCapture } from "@/components/PhotoCapture";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function CompleteJobDialog({ jobId, items: jobItems, open, onOpenChange, onDone }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [workDone, setWorkDone] = useState("");
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [signature, setSignature] = useState("");

  React.useEffect(() => {
    if (!open) return;
    setWorkDone("");
    setItems((jobItems || []).map((it) => ({
      component: it.component || it.description || "Work item",
      fixed: true,
      notes: "",
      photos: [],
    })));
    setMaterials([]);
    setPhotos([]);
    setSignature(user?.name || "");
  }, [open, jobItems, user?.name]);

  const setItem = (i, patch) => setItems((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addMaterial = () => setMaterials((s) => [...s, { name: "", notes: "", photos: [] }]);
  const setMaterial = (i, patch) => setMaterials((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const delMaterial = (i) => setMaterials((s) => s.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!workDone.trim()) { toast.error("Describe the work carried out."); return; }
    if (!signature.trim()) { toast.error("Type your name to sign off the job."); return; }
    setSaving(true);
    try {
      const payload = {
        work_done: workDone.trim(),
        completion_notes: "",
        item_completions: items.map((r) => ({
          component: r.component, fixed: !!r.fixed, notes: (r.notes || "").trim(), photos: r.photos || [],
        })),
        materials: materials
          .filter((m) => (m.name || "").trim())
          .map((m) => ({ name: m.name.trim(), notes: (m.notes || "").trim(), photos: m.photos || [] })),
        photos,
        parts_replaced: [],
        signature: signature.trim(),
        arrival_time: null,
        client_completion_id: (window.crypto?.randomUUID?.() || `web-${Date.now()}`),
      };
      const res = await maintenanceApi.complete(jobId, payload);
      toast.success("Job completed");
      onDone(res?.data || res);
    } catch (e) { toast.error(apiError(e, "Could not complete job")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="maintenance-complete-dialog">
        <DialogHeader>
          <DialogTitle>Complete maintenance job</DialogTitle>
          <DialogDescription>Record the work done, add photo evidence for each item, and sign off.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Work carried out *</Label>
            <Textarea rows={3} value={workDone} onChange={(e) => setWorkDone(e.target.value)} placeholder="Summarise what was done on site…" data-testid="complete-work-done" />
          </div>

          {items.length > 0 && (
            <div className="space-y-3">
              <Label className="flex items-center gap-1.5"><ClipboardCheck className="h-4 w-4 text-slate-500" /> Work items</Label>
              {items.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`complete-item-${i}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-slate-800">{r.component}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setItem(i, { fixed: true })}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${r.fixed ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}
                        data-testid={`complete-item-fixed-${i}`}
                      >Fixed</button>
                      <button
                        type="button"
                        onClick={() => setItem(i, { fixed: false })}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${!r.fixed ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-600"}`}
                        data-testid={`complete-item-notfixed-${i}`}
                      >Not fixed</button>
                    </div>
                  </div>
                  <Input className="mt-2" value={r.notes} onChange={(e) => setItem(i, { notes: e.target.value })} placeholder="Notes (optional)" data-testid={`complete-item-notes-${i}`} />
                  <div className="mt-2">
                    <PhotoCapture photos={r.photos} onChange={(p) => setItem(i, { photos: p })} testId={`complete-item-photos-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Package className="h-4 w-4 text-slate-500" /> Materials used</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addMaterial} data-testid="complete-material-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
            </div>
            {materials.length === 0 ? (
              <p className="text-xs text-slate-400">Optional — log parts/materials fitted, with photos.</p>
            ) : materials.map((m, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={`complete-material-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <Input value={m.name} onChange={(e) => setMaterial(i, { name: e.target.value })} placeholder="Material / part name" data-testid={`complete-material-name-${i}`} />
                    <Input value={m.notes} onChange={(e) => setMaterial(i, { notes: e.target.value })} placeholder="Notes (optional)" />
                    <PhotoCapture photos={m.photos} onChange={(p) => setMaterial(i, { photos: p })} testId={`complete-material-photos-${i}`} />
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:bg-red-50" onClick={() => delMaterial(i)} data-testid={`complete-material-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4 text-slate-500" /> Photos of completed works</Label>
            <PhotoCapture photos={photos} onChange={setPhotos} testId="complete-general-photos" />
          </div>

          <div className="space-y-1.5">
            <Label>Sign off (type your full name) *</Label>
            <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Your full name" data-testid="complete-signature" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="complete-submit">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Mark complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
